(() => {
    "use strict";

    const PANEL_SELECTOR = "[data-sobre-panel]";
    const SECTION_SELECTOR = ".sobre-site";
    const ALPHA_THRESHOLD = 24;
    const ROPE_LUMA_MIN = 42;
    const ROPE_ALPHA_MIN = 48;
    const MIN_FEATHER_AREA = 48;
    const BODY_SEEDS = [
        [0.38, 0.4],
        [0.34, 0.52],
        [0.38, 0.68],
    ];
    const TETHER_SEED = [0.30, 0.80];

    function activateSlide(panel, railId) {
        const tabs = panel.querySelectorAll(".sobre-site-rail-tab");
        const slides = panel.querySelectorAll(".sobre-site-slide");

        tabs.forEach((tab) => {
            const isActive = tab.getAttribute("data-sobre-rail") === railId;
            tab.setAttribute("aria-selected", isActive ? "true" : "false");
            tab.tabIndex = isActive ? 0 : -1;
        });

        slides.forEach((slide) => {
            const slideId = slide.id.replace("sobre-rail-panel-", "");
            const isActive = slideId === railId;

            slide.classList.toggle("is-active", isActive);
            slide.hidden = !isActive;
        });
    }

    function parseObjectPosition(value) {
        const parts = String(value || "50% 50%").trim().split(/\s+/);
        return {
            x: parts[0] || "50%",
            y: parts[1] || parts[0] || "50%",
        };
    }

    function resolveObjectOffset(align, available, rendered) {
        if (align === "left" || align === "0%") {
            return 0;
        }
        if (align === "right" || align === "100%") {
            return available - rendered;
        }
        if (align === "center" || align === "50%") {
            return (available - rendered) / 2;
        }
        if (align.endsWith("%")) {
            const percent = Number.parseFloat(align) / 100;
            if (!Number.isNaN(percent)) {
                return (available - rendered) * percent;
            }
        }
        return (available - rendered) / 2;
    }

    function emitInteractiveUsed(id) {
        if (!id) {
            return;
        }

        document.dispatchEvent(new CustomEvent("sobre:interactive-used", {
            detail: { id },
        }));
    }

    function getRenderedImageRect(img) {
        const box = img.getBoundingClientRect();
        const naturalWidth = img.naturalWidth;
        const naturalHeight = img.naturalHeight;

        if (!naturalWidth || !naturalHeight || box.width <= 0 || box.height <= 0) {
            return null;
        }

        const scale = Math.min(box.width / naturalWidth, box.height / naturalHeight);
        const width = naturalWidth * scale;
        const height = naturalHeight * scale;
        const style = window.getComputedStyle(img);
        const position = parseObjectPosition(style.objectPosition);
        const offsetX = resolveObjectOffset(position.x, box.width, width);
        const offsetY = resolveObjectOffset(position.y, box.height, height);

        return {
            left: box.left + offsetX,
            top: box.top + offsetY,
            width,
            height,
        };
    }

    function sampleImagePixel(img, canvas, clientX, clientY) {
        const rendered = getRenderedImageRect(img);
        if (!rendered) {
            return null;
        }

        const localX = clientX - rendered.left;
        const localY = clientY - rendered.top;

        if (localX < 0 || localY < 0 || localX > rendered.width || localY > rendered.height) {
            return null;
        }

        const sampleX = Math.min(
            canvas.width - 1,
            Math.max(0, Math.floor((localX / rendered.width) * canvas.width))
        );
        const sampleY = Math.min(
            canvas.height - 1,
            Math.max(0, Math.floor((localY / rendered.height) * canvas.height))
        );

        return { sampleX, sampleY, rendered };
    }

    function isRopeArtPixel(data, pixelIndex) {
        const alpha = data[pixelIndex + 3];
        if (alpha < ROPE_ALPHA_MIN) {
            return false;
        }

        const red = data[pixelIndex];
        const green = data[pixelIndex + 1];
        const blue = data[pixelIndex + 2];
        const luma = (red + green + blue) / 3;

        if (luma < ROPE_LUMA_MIN) {
            return false;
        }

        return red > green && red > blue + 8 && green > blue;
    }

    function buildRopeHitMask(imageData, width, height) {
        const data = imageData.data;
        const mask = new Uint8Array(width * height);
        let minX = width;
        let minY = height;
        let maxX = 0;
        let maxY = 0;

        for (let y = 0; y < height; y += 1) {
            for (let x = 0; x < width; x += 1) {
                const pixelIndex = (y * width + x) * 4;
                if (!isRopeArtPixel(data, pixelIndex)) {
                    continue;
                }

                const index = y * width + x;
                mask[index] = 1;
                minX = Math.min(minX, x);
                minY = Math.min(minY, y);
                maxX = Math.max(maxX, x);
                maxY = Math.max(maxY, y);
            }
        }

        if (maxX <= minX || maxY <= minY) {
            return { mask: null, bounds: null };
        }

        return {
            mask,
            bounds: { minX, minY, maxX, maxY },
        };
    }

    function getRopeOpaqueScreenRect(img, bounds, canvasWidth, canvasHeight) {
        const rendered = getRenderedImageRect(img);
        if (!rendered || !bounds) {
            return null;
        }

        const scaleX = rendered.width / canvasWidth;
        const scaleY = rendered.height / canvasHeight;

        return {
            left: rendered.left + bounds.minX * scaleX,
            top: rendered.top + bounds.minY * scaleY,
            right: rendered.left + bounds.maxX * scaleX,
            bottom: rendered.top + bounds.maxY * scaleY,
            width: (bounds.maxX - bounds.minX) * scaleX,
            height: (bounds.maxY - bounds.minY) * scaleY,
        };
    }

    function sampleRopePixel(img, ropeState, clientX, clientY) {
        if (!ropeState?.mask || !ropeState.bounds) {
            return null;
        }

        const rendered = getRenderedImageRect(img);
        if (!rendered) {
            return null;
        }

        const localX = clientX - rendered.left;
        const localY = clientY - rendered.top;

        if (localX < 0 || localY < 0 || localX > rendered.width || localY > rendered.height) {
            return null;
        }

        const sampleX = Math.min(
            ropeState.canvas.width - 1,
            Math.max(0, Math.floor((localX / rendered.width) * ropeState.canvas.width))
        );
        const sampleY = Math.min(
            ropeState.canvas.height - 1,
            Math.max(0, Math.floor((localY / rendered.height) * ropeState.canvas.height))
        );

        const index = sampleY * ropeState.canvas.width + sampleX;
        if (!ropeState.mask[index]) {
            return null;
        }

        return { sampleX, sampleY, rendered };
    }

    function findSeedPixel(imageData, width, height, centerX, centerY, radius) {
        const data = imageData.data;
        const indexAt = (x, y) => (y * width + x) * 4 + 3;

        for (let ring = 0; ring <= radius; ring += 1) {
            for (let offsetY = -ring; offsetY <= ring; offsetY += 1) {
                for (let offsetX = -ring; offsetX <= ring; offsetX += 1) {
                    const x = centerX + offsetX;
                    const y = centerY + offsetY;

                    if (x < 0 || y < 0 || x >= width || y >= height) {
                        continue;
                    }

                    if (data[indexAt(x, y)] >= ALPHA_THRESHOLD) {
                        return [x, y];
                    }
                }
            }
        }

        return null;
    }

    function floodFill(imageData, width, height, startX, startY, eligible) {
        const data = imageData.data;
        const visited = new Uint8Array(width * height);
        const pixels = [];
        const queue = [[startX, startY]];
        const indexAt = (x, y) => y * width + x;

        visited[indexAt(startX, startY)] = 1;

        while (queue.length) {
            const [x, y] = queue.pop();
            pixels.push([x, y]);

            if (x > 0) {
                const nx = x - 1;
                const ny = y;
                const ni = indexAt(nx, ny);
                if (!visited[ni] && eligible(nx, ny)) {
                    visited[ni] = 1;
                    queue.push([nx, ny]);
                }
            }
            if (x < width - 1) {
                const nx = x + 1;
                const ny = y;
                const ni = indexAt(nx, ny);
                if (!visited[ni] && eligible(nx, ny)) {
                    visited[ni] = 1;
                    queue.push([nx, ny]);
                }
            }
            if (y > 0) {
                const nx = x;
                const ny = y - 1;
                const ni = indexAt(nx, ny);
                if (!visited[ni] && eligible(nx, ny)) {
                    visited[ni] = 1;
                    queue.push([nx, ny]);
                }
            }
            if (y < height - 1) {
                const nx = x;
                const ny = y + 1;
                const ni = indexAt(nx, ny);
                if (!visited[ni] && eligible(nx, ny)) {
                    visited[ni] = 1;
                    queue.push([nx, ny]);
                }
            }
        }

        return { pixels, visited };
    }

    function buildBodyMask(imageData, width, height) {
        const mask = new Uint8Array(width * height);
        const data = imageData.data;
        const indexAt = (x, y) => y * width + x;

        BODY_SEEDS.forEach(([factorX, factorY]) => {
            const seed = findSeedPixel(
                imageData,
                width,
                height,
                Math.floor(width * factorX),
                Math.floor(height * factorY),
                28
            );

            if (!seed) {
                return;
            }

            const { pixels } = floodFill(
                imageData,
                width,
                height,
                seed[0],
                seed[1],
                (x, y) => data[(y * width + x) * 4 + 3] >= ALPHA_THRESHOLD
            );

            pixels.forEach(([x, y]) => {
                mask[indexAt(x, y)] = 1;
            });
        });

        return mask;
    }

    function buildConnectedComponents(imageData, width, height, bodyMask) {
        const data = imageData.data;
        const visited = new Uint8Array(width * height);
        const components = [];
        const indexAt = (x, y) => y * width + x;
        const isEligible = (x, y) => {
            const index = indexAt(x, y);
            return data[index * 4 + 3] >= ALPHA_THRESHOLD && bodyMask[index] === 0;
        };

        for (let y = 0; y < height; y += 1) {
            for (let x = 0; x < width; x += 1) {
                const index = indexAt(x, y);
                if (visited[index] || !isEligible(x, y)) {
                    continue;
                }

                const { pixels } = floodFill(
                    imageData,
                    width,
                    height,
                    x,
                    y,
                    (px, py) => isEligible(px, py) && visited[indexAt(px, py)] === 0
                );

                pixels.forEach(([px, py]) => {
                    visited[indexAt(px, py)] = 1;
                });

                if (pixels.length < MIN_FEATHER_AREA) {
                    continue;
                }

                let sumX = 0;
                let sumY = 0;
                const mask = new Uint8Array(width * height);
                pixels.forEach(([px, py]) => {
                    const pixelIndex = indexAt(px, py);
                    mask[pixelIndex] = 1;
                    sumX += px;
                    sumY += py;
                });

                components.push({
                    mask,
                    cx: sumX / pixels.length,
                    cy: sumY / pixels.length,
                    area: pixels.length,
                });
            }
        }

        return components;
    }

    function buildTetherMask(imageData, width, height, bodyMask, components) {
        const data = imageData.data;
        const indexAt = (x, y) => y * width + x;
        const seed = findSeedPixel(
            imageData,
            width,
            height,
            Math.floor(width * TETHER_SEED[0]),
            Math.floor(height * TETHER_SEED[1]),
            32
        );

        if (!seed) {
            return { components, tetherMask: null };
        }

        const isEligible = (x, y) => {
            const index = indexAt(x, y);
            return data[index * 4 + 3] >= ALPHA_THRESHOLD && bodyMask[index] === 0;
        };

        const { pixels } = floodFill(imageData, width, height, seed[0], seed[1], isEligible);
        if (pixels.length < 24) {
            return { components, tetherMask: null };
        }

        const tetherMask = new Uint8Array(width * height);
        pixels.forEach(([px, py]) => {
            tetherMask[indexAt(px, py)] = 1;
        });

        const remaining = components.filter((component) => {
            let overlap = 0;
            for (let i = 0; i < component.mask.length; i += 1) {
                if (component.mask[i] && tetherMask[i]) {
                    overlap += 1;
                }
            }
            return overlap < 16;
        });

        return { components: remaining, tetherMask };
    }

    function assignFeatherMasks(components, penasConfig, width, height) {
        const pool = components.slice();
        const assigned = [];

        penasConfig.forEach((pena) => {
            if (!pool.length) {
                return;
            }

            let matchIndex = 0;

            if (Array.isArray(pena.seed) && pena.seed.length >= 2) {
                const seedX = pena.seed[0] * width;
                const seedY = pena.seed[1] * height;
                let bestDistance = Infinity;

                pool.forEach((component, componentIndex) => {
                    const dx = component.cx - seedX;
                    const dy = component.cy - seedY;
                    const distance = dx * dx + dy * dy;
                    if (distance < bestDistance) {
                        bestDistance = distance;
                        matchIndex = componentIndex;
                    }
                });
            }

            const component = pool.splice(matchIndex, 1)[0];
            assigned.push({
                mask: component.mask,
                texto: pena.texto,
            });
        });

        pool.sort((a, b) => a.cy - b.cy || a.cx - b.cx);
        while (assigned.length < penasConfig.length && pool.length) {
            const component = pool.shift();
            const pena = penasConfig[assigned.length];
            assigned.push({
                mask: component.mask,
                texto: pena.texto,
            });
        }

        return assigned;
    }

    /** Executa `callback` uma única vez, na primeira interação do visitante (ou imediatamente se ela já aconteceu). */
    let interacted = false;
    const interactionQueue = [];
    function runOnFirstInteraction(callback) {
        if (interacted) {
            callback();
            return;
        }
        interactionQueue.push(callback);
        if (interactionQueue.length > 1) {
            return;
        }
        const events = ["pointerdown", "pointermove", "touchstart", "keydown", "wheel", "scroll"];
        const fire = (event) => {
            if (event.type === "scroll" && event.target !== document) {
                return;
            }
            interacted = true;
            events.forEach((type) => window.removeEventListener(type, fire, true));
            window.setTimeout(() => interactionQueue.splice(0).forEach((queued) => queued()), 0);
        };
        events.forEach((type) => window.addEventListener(type, fire, { capture: true, passive: true }));
    }

    function loadImageHitMap(img) {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d", { willReadFrequently: true });

        return new Promise((resolve) => {
            const draw = () => {
                if (!img.naturalWidth || !img.naturalHeight || !context) {
                    resolve(null);
                    return;
                }

                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                context.clearRect(0, 0, canvas.width, canvas.height);
                context.drawImage(img, 0, 0);
                resolve({ canvas, context, imageData: context.getImageData(0, 0, canvas.width, canvas.height) });
            };

            if (img.complete) {
                draw();
            } else {
                img.addEventListener("load", draw, { once: true });
            }
        });
    }

    function bindSectionArt(section) {
        if (section.dataset.artBound === "true") {
            return;
        }

        const artConfigRaw = section.getAttribute("data-sobre-art") || "{}";
        let artConfig = {};

        try {
            artConfig = JSON.parse(artConfigRaw);
        } catch (error) {
            artConfig = {};
        }

        const birdImg = section.querySelector(".sobre-site-figure-bird");
        const ropeImg = section.querySelector(".sobre-site-figure-rope");
        const birdHit = section.querySelector("[data-sobre-bird-hit]");
        const featherHint = section.querySelector("[data-sobre-hint-feather]");
        const featherQuote = section.querySelector("[data-sobre-hint-feather-quote]");
        const ropeHint = section.querySelector("[data-sobre-hint-rope]");
        const ropeBody = section.querySelector("[data-sobre-hint-rope-body]");
        const ropeClose = section.querySelector("[data-sobre-hint-rope-close]");
        const featherClose = section.querySelector("[data-sobre-hint-feather-close]");
        const cta = birdHit ? birdHit.querySelector(".sobre-site-bird-cta") : null;

        const mobileRopeHintQuery = window.matchMedia("(max-width: 1080px)");

        function usesMobileRopeHint() {
            return mobileRopeHintQuery.matches;
        }

        function setHintBackdrop(isOpen) {
            document.body.classList.toggle("sobre-sec2-hint-open", isOpen);
        }

        function applyMobileModalHint(hintEl, closeBtn) {
            if (!hintEl._modalParent) {
                hintEl._modalParent = hintEl.parentElement;
            }

            if (hintEl.parentElement !== document.body) {
                document.body.appendChild(hintEl);
            }

            hintEl.style.position = "fixed";
            hintEl.style.left = "50%";
            hintEl.style.top = "50%";
            hintEl.style.right = "auto";
            hintEl.style.bottom = "auto";
            hintEl.style.transform = "translate(-50%, -50%)";
            hintEl.style.zIndex = "1200";
            hintEl.classList.add("is-mobile-modal");
            if (closeBtn) {
                closeBtn.hidden = false;
            }
            setHintBackdrop(true);
        }

        function clearMobileModalHint(hintEl, closeBtn) {
            hintEl.style.position = "";
            hintEl.style.left = "";
            hintEl.style.top = "";
            hintEl.style.right = "";
            hintEl.style.bottom = "";
            hintEl.style.transform = "";
            hintEl.style.zIndex = "";
            hintEl.classList.remove("is-mobile-modal");
            if (closeBtn) {
                closeBtn.hidden = true;
            }

            if (hintEl._modalParent && hintEl.parentElement === document.body) {
                hintEl._modalParent.appendChild(hintEl);
            }
        }

        if (!birdImg && !ropeImg) {
            return;
        }

        section.dataset.artBound = "true";

        const state = {
            bodyMask: null,
            featherMasks: [],
            tetherMask: null,
            tetherTexto: artConfig.corda_passaro || "",
            cordaNos: Array.isArray(artConfig.corda_nos) ? artConfig.corda_nos : [],
            birdCanvas: null,
            ropeCanvas: null,
            ropeMask: null,
            ropeBounds: null,
            activeFeather: false,
            activeRope: false,
            ropePinned: false,
            featherPinned: false,
        };

        function hideFeatherHint() {
            if (!featherHint) {
                return;
            }

            featherHint.classList.remove("is-visible", "is-pinned");
            featherHint.hidden = true;
            clearMobileModalHint(featherHint, featherClose);
            if (featherQuote) {
                featherQuote.textContent = "";
            }
            state.activeFeather = false;
            state.featherPinned = false;
            section.removeAttribute("data-feather-pinned");
            birdHit?.classList.remove("is-feather-active");
            if (!state.activeRope) {
                setHintBackdrop(false);
            }
        }

        function hideRopeHint() {
            if (!ropeHint) {
                return;
            }

            setRopePointerActive(false);

            ropeHint.classList.remove("is-visible", "is-pinned", "is-anchor-left", "is-anchor-right");
            ropeHint.hidden = true;
            clearMobileModalHint(ropeHint, ropeClose);
            if (ropeBody) {
                ropeBody.textContent = "";
            }
            state.activeRope = false;
            state.ropePinned = false;
            section.removeAttribute("data-rope-pinned");
            if (!state.activeFeather) {
                setHintBackdrop(false);
            }
        }

        function showFeatherHint(text, options = {}) {
            if (!featherHint || !featherQuote || text === "") {
                return;
            }

            hideRopeHint();
            featherQuote.textContent = text;
            positionFeatherHint(options);
            featherHint.hidden = false;
            featherHint.classList.add("is-visible");
            state.activeFeather = true;
            birdHit?.classList.add("is-feather-active");
            setBirdHot(false);
        }

        function pinFeatherHint(text) {
            showFeatherHint(text, { pin: true });
            state.featherPinned = true;
            section.setAttribute("data-feather-pinned", "true");
            featherHint?.classList.add("is-pinned");
            if (featherClose) {
                featherClose.hidden = false;
            }
            if (usesMobileRopeHint()) {
                setHintBackdrop(true);
            }
            birdHit?.blur();
            setBirdHot(false);
        }

        function showRopeHint(text, options = {}) {
            if (!ropeHint || text === "") {
                return;
            }

            if (!options.pin && state.ropePinned) {
                return;
            }

            hideFeatherHint();
            if (ropeBody) {
                ropeBody.textContent = text;
            }
            positionRopeHint(options);
            ropeHint.hidden = false;
            ropeHint.classList.add("is-visible");
            state.activeRope = true;
            setBirdHot(false);
        }

        function pinRopeHint(text, options = {}) {
            showRopeHint(text, { ...options, pin: true });
            state.ropePinned = true;
            section.setAttribute("data-rope-pinned", "true");
            ropeHint?.classList.add("is-pinned");
            if (ropeClose) {
                ropeClose.hidden = false;
            }
            if (usesMobileRopeHint()) {
                setHintBackdrop(true);
            }
        }

        function positionRopeHint(options = {}) {
            if (!ropeHint || !ropeImg || !state.ropeBounds || !state.ropeCanvas) {
                return;
            }

            const sectionRect = section.getBoundingClientRect();
            const opaque = getRopeOpaqueScreenRect(
                ropeImg,
                state.ropeBounds,
                state.ropeCanvas.canvas.width,
                state.ropeCanvas.canvas.height
            );

            if (!opaque) {
                return;
            }

            ropeHint.hidden = false;
            const hintWidth = ropeHint.offsetWidth || 256;
            const gap = 16;
            const minLeft = 16;
            const minTop = 72;
            const maxTop = Math.max(minTop, sectionRect.height - 72);

            ropeHint.classList.remove("is-anchor-left", "is-anchor-right", "is-mobile-modal");
            ropeHint.style.transform = "";

            if (usesMobileRopeHint()) {
                applyMobileModalHint(ropeHint, ropeClose);
                return;
            }

            let anchorScreenY = opaque.top + opaque.height / 2;

            const top = Math.min(
                Math.max(anchorScreenY - sectionRect.top, minTop),
                maxTop
            );
            let left = opaque.right - sectionRect.left + gap;
            const maxLeft = Math.max(minLeft, sectionRect.width - hintWidth - 16);
            left = Math.max(minLeft, Math.min(left, maxLeft));

            ropeHint.style.top = `${top}px`;
            ropeHint.style.left = `${left}px`;
            ropeHint.style.right = "auto";
            ropeHint.classList.add("is-anchor-right");
            if (ropeClose) {
                ropeClose.hidden = !options.pin;
            }
        }

        function positionFeatherHint(options = {}) {
            if (!featherHint) {
                return;
            }

            if (usesMobileRopeHint()) {
                applyMobileModalHint(featherHint, featherClose);
                return;
            }

            if (options.pin && featherClose) {
                featherClose.hidden = false;
            }
        }

        function setBirdHot(isHot) {
            if (!birdHit) {
                return;
            }

            if (state.activeFeather || state.featherPinned) {
                isHot = false;
            }

            birdHit.classList.toggle("is-bird-hot", isHot);

            if (!cta) {
                return;
            }

            cta.setAttribute("aria-hidden", isHot ? "false" : "true");
            cta.tabIndex = isHot ? 0 : -1;
        }

        function resolveBirdHit(clientX, clientY) {
            if (!birdImg || !state.birdCanvas) {
                return null;
            }

            const birdSample = sampleImagePixel(birdImg, state.birdCanvas.canvas, clientX, clientY);
            if (!birdSample) {
                return null;
            }

            return hitBirdLayers(birdSample.sampleX, birdSample.sampleY);
        }

        function ropeMessageForY(sampleY) {
            if (!state.cordaNos.length || !state.ropeBounds) {
                return "";
            }

            const { minY, maxY } = state.ropeBounds;
            const span = Math.max(maxY - minY, 1);
            const ratioFromBottom = 1 - ((sampleY - minY) / span);
            const band = Math.min(
                state.cordaNos.length - 1,
                Math.floor(ratioFromBottom * state.cordaNos.length)
            );
            return state.cordaNos[band] || state.cordaNos[state.cordaNos.length - 1];
        }

        function ropeBandIndexForY(sampleY) {
            if (!state.cordaNos.length || !state.ropeBounds) {
                return -1;
            }

            const { minY, maxY } = state.ropeBounds;
            const span = Math.max(maxY - minY, 1);
            const ratioFromBottom = 1 - ((sampleY - minY) / span);
            return Math.min(
                state.cordaNos.length - 1,
                Math.floor(ratioFromBottom * state.cordaNos.length)
            );
        }

        function ropeSampleYForBandIndex(bandIndex) {
            if (!state.ropeBounds || bandIndex < 0) {
                return null;
            }

            const bandCount = Math.max(1, state.cordaNos.length);
            const { minY, maxY } = state.ropeBounds;
            const spanY = Math.max(maxY - minY, 1);
            const bandHeight = spanY / bandCount;
            const clampedIndex = Math.min(bandCount - 1, Math.max(0, bandIndex));

            return maxY - bandHeight * (clampedIndex + 0.5);
        }

        function ropeHintIdForBandIndex(bandIndex) {
            if (bandIndex < 0) {
                return "";
            }

            return `art-rope-${bandIndex + 1}`;
        }

        function hitBirdLayers(sampleX, sampleY) {
            if (!state.birdCanvas) {
                return null;
            }

            const { width, height } = state.birdCanvas.canvas;
            const index = sampleY * width + sampleX;

            if (state.tetherMask && state.tetherMask[index]) {
                return { type: "tether", texto: state.tetherTexto };
            }

            for (let i = 0; i < state.featherMasks.length; i += 1) {
                if (state.featherMasks[i].mask[index]) {
                    return { type: "feather", texto: state.featherMasks[i].texto };
                }
            }

            if (state.bodyMask && state.bodyMask[index]) {
                return { type: "body" };
            }

            return null;
        }

        function setRopePointerActive(active) {
            section.classList.toggle("is-rope-pointer", Boolean(active));
        }

        function updatePointer(clientX, clientY) {
            const sectionRect = section.getBoundingClientRect();
            const insideSection = clientX >= sectionRect.left
                && clientX <= sectionRect.right
                && clientY >= sectionRect.top
                && clientY <= sectionRect.bottom;

            if (!insideSection) {
                setRopePointerActive(false);
                if (!state.featherPinned) {
                    hideFeatherHint();
                }
                if (!state.ropePinned) {
                    hideRopeHint();
                }
                if (!state.featherPinned) {
                    setBirdHot(false);
                }
                return;
            }

            if (state.ropePinned || state.featherPinned) {
                setRopePointerActive(false);
                return;
            }

            if (ropeImg && state.ropeCanvas && state.ropeMask) {
                const ropeSample = sampleRopePixel(ropeImg, {
                    mask: state.ropeMask,
                    bounds: state.ropeBounds,
                    canvas: state.ropeCanvas.canvas,
                }, clientX, clientY);

                if (ropeSample) {
                    setRopePointerActive(true);
                    showRopeHint(ropeMessageForY(ropeSample.sampleY));
                    return;
                }
            }

            setRopePointerActive(false);
            hideRopeHint();

            if (birdImg && state.birdCanvas) {
                const hit = resolveBirdHit(clientX, clientY);
                if (hit) {
                    if (hit.type === "feather" || hit.type === "tether") {
                        showFeatherHint(hit.texto);
                        return;
                    }

                    hideFeatherHint();
                    setBirdHot(hit.type === "body");
                    return;
                }
            }

            hideFeatherHint();
            setBirdHot(false);
        }

                // O mapa de pixels (canvas + getImageData + máscaras) custava centenas de ms no carregamento e só serve para o hover/toque
        // na arte: espera a primeira interação do visitante.
        runOnFirstInteraction(() => Promise.all([
            birdImg ? loadImageHitMap(birdImg) : Promise.resolve(null),
            ropeImg ? loadImageHitMap(ropeImg) : Promise.resolve(null),
        ]).then(([birdMap, ropeMap]) => {
            if (birdMap) {
                const { imageData, canvas } = birdMap;
                const width = canvas.width;
                const height = canvas.height;
                state.birdCanvas = birdMap;
                state.bodyMask = buildBodyMask(imageData, width, height);

                let components = buildConnectedComponents(imageData, width, height, state.bodyMask);
                const tetherResult = buildTetherMask(imageData, width, height, state.bodyMask, components);
                components = tetherResult.components;
                state.tetherMask = tetherResult.tetherMask;

                const penasConfig = Array.isArray(artConfig.penas) ? artConfig.penas : [];
                components.sort((a, b) => a.cy - b.cy || a.cx - b.cx);
                state.featherMasks = assignFeatherMasks(components, penasConfig, width, height);
            }

            if (ropeMap) {
                state.ropeCanvas = ropeMap;
                const ropeHit = buildRopeHitMask(
                    ropeMap.imageData,
                    ropeMap.canvas.width,
                    ropeMap.canvas.height
                );
                state.ropeMask = ropeHit.mask;
                state.ropeBounds = ropeHit.bounds;
            }

            function interactiveIdForBirdHit(hit) {
                if (!hit) {
                    return "";
                }

                if (hit.type === "tether") {
                    return "art-tether";
                }

                if (hit.type === "body") {
                    return "art-bird-body";
                }

                if (hit.type === "feather") {
                    const featherIndex = state.featherMasks.findIndex((feather) => feather.texto === hit.texto);
                    if (featherIndex >= 0) {
                        return `art-feather-${featherIndex + 1}`;
                    }
                }

                return "";
            }

            function interactiveIdForRopeY(sampleY) {
                return ropeHintIdForBandIndex(ropeBandIndexForY(sampleY));
            }

            function activateRopeBand(bandIndex) {
                if (bandIndex < 0 || bandIndex >= state.cordaNos.length) {
                    return;
                }

                pinRopeHint(state.cordaNos[bandIndex]);

                emitInteractiveUsed(ropeHintIdForBandIndex(bandIndex));
            }

            function activateArtPoint(clientX, clientY, hintId = "") {
                if (typeof hintId === "string" && hintId.startsWith("art-rope-")) {
                    const bandIndex = parseInt(hintId.slice("art-rope-".length), 10) - 1;
                    if (!Number.isNaN(bandIndex)) {
                        activateRopeBand(bandIndex);
                        return;
                    }
                }

                if (ropeImg && state.ropeCanvas && state.ropeMask) {
                    const ropeSample = sampleRopePixel(ropeImg, {
                        mask: state.ropeMask,
                        bounds: state.ropeBounds,
                        canvas: state.ropeCanvas.canvas,
                    }, clientX, clientY);

                    if (ropeSample) {
                        activateRopeBand(ropeBandIndexForY(ropeSample.sampleY));
                        return;
                    }
                }

                const hit = resolveBirdHit(clientX, clientY);
                if (hit?.type === "feather" || hit?.type === "tether") {
                    pinFeatherHint(hit.texto);
                    emitInteractiveUsed(interactiveIdForBirdHit(hit));
                    return;
                }

                if (hit?.type === "body") {
                    setBirdHot(true);
                    emitInteractiveUsed("art-bird-body");
                }
            }

            document.dispatchEvent(new CustomEvent("sobre:art-hints-ready", {
                detail: {
                    foldEl: section.querySelector(".sobre-site-art-fold"),
                    birdImg,
                    ropeImg,
                    birdHit,
                    ropeStage: section.querySelector(".sobre-site-art-stage"),
                    featherMasks: state.featherMasks,
                    tetherMask: state.tetherMask,
                    bodyMask: state.bodyMask,
                    birdImageData: state.birdCanvas?.imageData,
                    ropeImageData: state.ropeCanvas?.imageData,
                    ropeMask: state.ropeMask,
                    ropeBounds: state.ropeBounds,
                    canvasWidth: state.birdCanvas?.canvas?.width || 0,
                    canvasHeight: state.birdCanvas?.canvas?.height || 0,
                    ropeCanvasWidth: state.ropeCanvas?.canvas?.width || 0,
                    ropeCanvasHeight: state.ropeCanvas?.canvas?.height || 0,
                    cordaNos: state.cordaNos,
                    onSpotActivate: activateArtPoint,
                },
            }));

            document.addEventListener("mousemove", (event) => {
                updatePointer(event.clientX, event.clientY);
            }, { passive: true });

            section.addEventListener("pointerdown", (event) => {
                if (event.target.closest("[data-sobre-hint-rope], .sobre-site-hint-close")) {
                    return;
                }

                if (ropeImg && state.ropeCanvas && state.ropeMask) {
                    const ropeSample = sampleRopePixel(ropeImg, {
                        mask: state.ropeMask,
                        bounds: state.ropeBounds,
                        canvas: state.ropeCanvas.canvas,
                    }, event.clientX, event.clientY);

                    if (ropeSample) {
                        event.preventDefault();
                        event.stopPropagation();
                        activateRopeBand(ropeBandIndexForY(ropeSample.sampleY));
                        return;
                    }
                }

                const hit = resolveBirdHit(event.clientX, event.clientY);
                if (hit?.type === "feather" || hit?.type === "tether") {
                    event.preventDefault();
                    event.stopPropagation();
                    pinFeatherHint(hit.texto);
                    emitInteractiveUsed(interactiveIdForBirdHit(hit));
                    return;
                }

                if (state.featherPinned && !event.target.closest("[data-sobre-hint-feather]")) {
                    hideFeatherHint();
                }
            });

            document.addEventListener("pointerdown", (event) => {
                if (state.ropePinned && ropeHint) {
                    const insideArtInteraction = event.target.closest(
                        ".sobre-site-art-fold, .sobre-site-art-stage, [data-sobre-hint-rope], .sobre-art-hint-hit, [data-sobre-bird-hit]"
                    );

                    if (!insideArtInteraction) {
                        hideRopeHint();
                    }
                }

                if (!state.featherPinned || !featherHint) {
                    return;
                }

                if (event.target.closest("[data-sobre-hint-feather], [data-sobre-bird-hit]")) {
                    return;
                }

                hideFeatherHint();
            });

            if (ropeClose) {
                ropeClose.addEventListener("click", (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    hideRopeHint();
                });
            }

            if (featherClose) {
                featherClose.addEventListener("click", (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    hideFeatherHint();
                });
            }

            bindFoldScrollHints(section);

            section.addEventListener("touchstart", (event) => {
                const touch = event.touches[0];
                if (!touch) {
                    return;
                }

                updatePointer(touch.clientX, touch.clientY);
            }, { passive: true });

            section.addEventListener("touchend", () => {
                if (!state.ropePinned) {
                    hideRopeHint();
                }
                if (!state.featherPinned) {
                    hideFeatherHint();
                }
                if (!state.featherPinned) {
                    setBirdHot(false);
                }
            }, { passive: true });

            mobileRopeHintQuery.addEventListener("change", () => {
                hideRopeHint();
                hideFeatherHint();
            });
        }));
    }

    function scrollToFoldTarget(target) {
        if (!target) {
            return;
        }

        const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches
            ? "auto"
            : "smooth";
        const root = document.documentElement;
        root.classList.add("is-fold-scroll-pending");

        target.scrollIntoView({
            behavior,
            block: "start",
        });

        const releaseSnap = () => {
            root.classList.remove("is-fold-scroll-pending");
        };

        if (behavior === "auto") {
            releaseSnap();
            return;
        }

        if ("onscrollend" in window) {
            window.addEventListener("scrollend", releaseSnap, { once: true });
        }

        window.setTimeout(releaseSnap, 900);
    }

    function bindFoldScrollHints(root) {
        const scope = root || document;
        scope.querySelectorAll("[data-sobre-fold-scroll-target]").forEach((trigger) => {
            if (trigger.dataset.foldScrollBound === "true") {
                return;
            }

            trigger.dataset.foldScrollBound = "true";
            trigger.addEventListener("click", (event) => {
                event.preventDefault();
                const selector = trigger.getAttribute("data-sobre-fold-scroll-target") || "";
                if (selector === "") {
                    return;
                }

                scrollToFoldTarget(document.querySelector(selector));
            });
        });
    }

    function bindPanel(panel) {
        if (panel.dataset.bound === "true") {
            return;
        }

        panel.dataset.bound = "true";

        const tabs = panel.querySelectorAll(".sobre-site-rail-tab");
        if (!tabs.length) {
            return;
        }

        tabs.forEach((tab) => {
            tab.addEventListener("click", () => {
                const railId = tab.getAttribute("data-sobre-rail");
                if (!railId) {
                    return;
                }

                activateSlide(panel, railId);
            });

            tab.addEventListener("keydown", (event) => {
                const items = Array.from(tabs);
                const currentIndex = items.indexOf(tab);
                if (currentIndex < 0) {
                    return;
                }

                let nextIndex = currentIndex;

                if (event.key === "ArrowDown" || event.key === "ArrowRight") {
                    event.preventDefault();
                    nextIndex = (currentIndex + 1) % items.length;
                } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
                    event.preventDefault();
                    nextIndex = (currentIndex - 1 + items.length) % items.length;
                } else if (event.key === "Home") {
                    event.preventDefault();
                    nextIndex = 0;
                } else if (event.key === "End") {
                    event.preventDefault();
                    nextIndex = items.length - 1;
                } else {
                    return;
                }

                const nextTab = items[nextIndex];
                const railId = nextTab.getAttribute("data-sobre-rail");
                if (!railId) {
                    return;
                }

                activateSlide(panel, railId);
                nextTab.focus();
            });
        });
    }

    function boot() {
        document.querySelectorAll(PANEL_SELECTOR).forEach(bindPanel);
        document.querySelectorAll(SECTION_SELECTOR).forEach(bindSectionArt);
    }

    window.__sobreGetRenderedImageRect = getRenderedImageRect;
    window.siteReady(boot);
})();
